/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useGlobalHeader} from '../../contexts/global-elements-context'
import {
    useMultiStyleConfig,
    Box,
    Flex,
    IconButton,
    Text,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'

import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useMiniCartContext} from '../../contexts/mini-cart-context'
import {useHeaderContext} from '../../contexts/header-context'
import {useHeaderHeight} from '../../hooks/use-header-height'
import {useStickyState} from '../../contexts/sticky-context'
import {useBrand} from '../../contexts/brand-context'
import withRegistration from '../with-registration'
import {DynBrandLogo, BrandLogo} from '../../components/icons'
import CmsIcon from './partial/cmsIcon'
import CmsHeaderLogo from './partial/cmsHeaderLogo'
import useTransparentHeader from './partial/useTransparentHeader'
import {useGetLinkData} from '../../contexts/category-url-context'
import {resolveGDILink} from '../optimizely/gdi/utils/gdi-link-resolver'
import {getExpandedFromContentArea} from '../optimizely/gdi/utils/gdi-content-area'
import {HEADER_CONTENT_STYLE} from './config'

import {MiniCartIcon, Account, HeartIcon, GeneralSearchIcon, HeaderMenuIcon} from '../icons'

import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useSearchModal} from '../../contexts/search-modal-context'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {getPreviewSession} from '../../utils/optimizely-preview'

const IconButtonWithRegistration = withRegistration(IconButton)
const WishlistIconButtonWithRegistration = withRegistration(IconButton, {isWishlistAction: true})

const Header = ({
    children,
    isCart = false,
    onMenuClick = noop,
    onMyAccountClick = noop,
    onLogoClick = noop,
    onMyCartClick = noop,
    onWishlistClick = noop,
    onStoreLocatorClick = noop,
    ...props
}) => {
    const intl = useIntl()
    const {headerRef, shouldShowSticky, isScrolled, shouldShowHeader} = useHeaderContext()
    const {
        derivedData: {totalItems},
        data: basket
    } = useCurrentBasket()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {openSearchModal} = useSearchModal()
    const {showCustomToast} = useCustomToast()
    const cartToastIdRef = useRef(null)
    const {isDynamite} = useBrand()
    const styles = useMultiStyleConfig('Header')

    // Transparent header variant logic (hover, gradient, filters, etc.)
    const {
        isTransparent,
        getContainerVariantStyle,
        textColor,
        logoFilterStyle,
        cmsIconColorFilter,
        gradientBackground,
        dropShadowStyle
    } = useTransparentHeader(styles)

    // CMS-driven content
    const {data: headerData} = useGlobalHeader()
    const getLinkData = useGetLinkData()

    // New CMS: Logo / Icons / HeaderVariations groups
    const cmsIcons = headerData?.Icons || {}
    const logoImageBlock = headerData?.Logo?.imageBlock || null
    const logoLinkBlock = getExpandedFromContentArea(
        logoImageBlock?.GDIImageBlockLink || logoImageBlock?.Link
    )

    // Legacy IconNavList-based data is no longer surfaced by the new CMS shape; keep
    // these as undefined so the existing JSX falls back to its built-in defaults.
    const searchPlaceholder = undefined
    const searchIconData = undefined
    const accountIconData = undefined
    const cartIconData = undefined
    const wishlistIconData = undefined
    const cmsLogoLinkData = logoLinkBlock ? resolveGDILink(logoLinkBlock, getLinkData) : null

    // Build logo IconButton link props: CMS link wins over onLogoClick fallback
    const logoButtonLinkProps = cmsLogoLinkData
        ? cmsLogoLinkData.isExternal
            ? {as: 'a', href: cmsLogoLinkData.href, target: cmsLogoLinkData.target, rel: cmsLogoLinkData.rel}
            : {as: Link, href: cmsLogoLinkData.href}
        : {onClick: onLogoClick}

    // Resolve LinkOverride for each icon via the same GDILinkBlock resolver as the logo.
    // Returns IconButton-ready props (or null when no CMS override is authored).
    const buildCmsIconLinkProps = (iconKey) => {
        const linkBlock = cmsIcons[iconKey]?.linkBlock
        if (!linkBlock) return null
        const linkData = resolveGDILink(linkBlock, getLinkData)
        if (!linkData) return null
        return linkData.isExternal
            ? {as: 'a', href: linkData.href, target: linkData.target, rel: linkData.rel}
            : {as: Link, href: linkData.href}
    }
    const searchIconLinkProps = buildCmsIconLinkProps('search')
    const accountIconLinkProps = buildCmsIconLinkProps('account')
    const wishlistIconLinkProps = buildCmsIconLinkProps('wishlist')
    const cartIconLinkProps = buildCmsIconLinkProps('cart')

    const {onOpen: onOpenMiniCartOpen} = useMiniCartContext()
    useHeaderHeight()

    const {pdpStickyButtonActive} = useStickyState()

    const headerContentStyle = shouldShowHeader
        ? HEADER_CONTENT_STYLE.visible
        : HEADER_CONTENT_STYLE.hidden

    const LogoComponent = (
        <CmsHeaderLogo
            logoImageBlock={logoImageBlock}
            FallbackComponent={isDynamite ? DynBrandLogo : BrandLogo}
            fallbackProps={isDynamite ? styles.dynLogo : styles.logo}
        />
    )

    return (
        <Box
            id="app-header"
            {...styles.container}
            {...(!isTransparent && isScrolled && styles.containerSticky)}
            {...getContainerVariantStyle()}
            color={textColor}
            {...props}
            ref={headerRef}
        >
            <Box style={headerContentStyle}>
                {gradientBackground && (
                    <Box {...styles.gradientOverlay} background={gradientBackground} />
                )}
                <Box
                    {...styles.content}
                    {...styles.headerContent}
                    style={dropShadowStyle}
                >
                <Flex {...styles.mainRow}>
                    <Flex {...styles.leftSection}>
                        <HideOnDesktop>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu',
                                    defaultMessage: 'Menu'
                                })}
                                title={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu.open_dialog',
                                    defaultMessage: 'Opens a dialog'
                                })}
                                icon={<HeaderMenuIcon />}
                                {...styles.menuButton}
                                onClick={onMenuClick}
                                data-testid="header-menu-button"
                            />
                            <IconButton
                                aria-label={
                                    cmsIcons.search?.ariaLabel ||
                                    intl.formatMessage({
                                        id: 'header.button.assistive_msg.search',
                                        defaultMessage: 'Search'
                                    })
                                }
                                icon={
                                    <CmsIcon
                                        iconUrl={cmsIcons.search?.iconUrl}
                                        iconData={searchIconData}
                                        FallbackIcon={GeneralSearchIcon}
                                        iconStyles={styles.iconButton}
                                        colorFilter={cmsIconColorFilter}
                                    />
                                }
                                {...styles.searchButtonMobile}
                                data-testid="header-button-search-mobile"
                                {...(searchIconLinkProps || {onClick: () => openSearchModal(searchPlaceholder)})}
                            />
                        </HideOnDesktop>

                        <HideOnMobile>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.logo',
                                    defaultMessage: 'Logo'
                                })}
                                icon={LogoComponent}
                                {...styles.logoButton}
                                style={logoFilterStyle}
                                data-testid="header-logo"
                                {...logoButtonLinkProps}
                            />
                        </HideOnMobile>
                    </Flex>

                    <Flex {...styles.centerSection}>
                        <HideOnMobile>
                            <Box {...styles.bodyContainer}>{children}</Box>
                        </HideOnMobile>

                        <HideOnDesktop>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.logo',
                                    defaultMessage: 'Logo'
                                })}
                                icon={LogoComponent}
                                {...styles.logoButton}
                                style={logoFilterStyle}
                                data-testid="header-logo"
                                {...logoButtonLinkProps}
                            />
                        </HideOnDesktop>
                    </Flex>

                    <Flex {...styles.rightSection}>
                        <HideOnMobile>
                            <IconButton
                                data-testid="header-search-button"
                                as={Link}
                                href={'#'}
                                aria-label={
                                    cmsIcons.search?.ariaLabel ||
                                    intl.formatMessage({
                                        id: 'header.button.open_search_drawer',
                                        defaultMessage: 'Open search drawer'
                                    })
                                }
                                icon={
                                    <CmsIcon
                                        iconUrl={cmsIcons.search?.iconUrl}
                                        iconData={searchIconData}
                                        FallbackIcon={GeneralSearchIcon}
                                        iconStyles={styles.iconButton}
                                        colorFilter={cmsIconColorFilter}
                                    />
                                }
                                {...styles.searchButtonDesktop}
                                {...(searchIconLinkProps || {
                                    onClick: (e) => {
                                        if (e) e.preventDefault()
                                        openSearchModal(searchPlaceholder)
                                    }
                                })}
                            />
                        </HideOnMobile>

                        <HideOnMobile>
                            {/* Only authenticated users navigate to the wishlist page; the
                              * `withRegistration` HOC opens the Login modal for unauthenticated
                              * users. When a CMS link override is authored, render a plain
                              * IconButton so the authored link is followed directly. */}
                            {(() => {
                                const WishlistButton = wishlistIconLinkProps
                                    ? IconButton
                                    : WishlistIconButtonWithRegistration
                                return (
                                    <WishlistButton
                                        data-testid="header-wishlist-button"
                                        as={Link}
                                        href="/account/wishlist"
                                        aria-label={
                                            cmsIcons.wishlist?.ariaLabel ||
                                            intl.formatMessage({
                                                defaultMessage: 'Go to Wishlist page',
                                                id: 'header.button.assistive_msg.wishlist'
                                            })
                                        }
                                        icon={
                                            <CmsIcon
                                                iconUrl={cmsIcons.wishlist?.iconUrl}
                                                iconData={wishlistIconData}
                                                FallbackIcon={HeartIcon}
                                                iconStyles={styles.iconButton}
                                                fallbackProps={{'aria-hidden': null, 'aria-label': null}}
                                                colorFilter={cmsIconColorFilter}
                                            />
                                        }
                                        {...styles.wishlistButton}
                                        {...(wishlistIconLinkProps || {onClick: onWishlistClick})}
                                    />
                                )
                            })()}
                        </HideOnMobile>

                        {/* When a CMS link override is authored, render a plain IconButton so
                          * the link is followed directly. The `withRegistration` HOC always
                          * intercepts the click (preventDefault + open auth modal for
                          * unregistered users), which would override the authored link. */}
                        {(() => {
                            const AccountButton = accountIconLinkProps
                                ? IconButton
                                : IconButtonWithRegistration
                            return (
                                <AccountButton
                                    data-testid="header-account-button"
                                    as={Link}
                                    href="/account"
                                    icon={
                                        <CmsIcon
                                            iconUrl={cmsIcons.account?.iconUrl}
                                            iconData={accountIconData}
                                            FallbackIcon={Account}
                                            iconStyles={styles.iconButton}
                                            colorFilter={cmsIconColorFilter}
                                        />
                                    }
                                    aria-label={
                                        cmsIcons.account?.ariaLabel ||
                                        intl.formatMessage({
                                            id: 'header.button.assistive_msg.my_account',
                                            defaultMessage: 'Open my account modal'
                                        })
                                    }
                                    {...styles.accountIcon}
                                    {...(accountIconLinkProps || {
                                        onClick: (e) => {
                                            if (e) e.preventDefault()
                                            onMyAccountClick()
                                        }
                                    })}
                                />
                            )
                        })()}

                        <IconButton
                            data-testid="header-cart-button"
                            as={Link}
                            href="/cart"
                            aria-label={intl.formatMessage(
                                {
                                    id: 'header.button.assistive_msg.my_cart_with_num_items',
                                    defaultMessage: 'My cart, number of items: {numItems}'
                                },
                                {numItems: totalItems}
                            )}
                            icon={
                                <>
                                    <CmsIcon
                                        iconUrl={cmsIcons.cart?.iconUrl}
                                        iconData={cartIconData}
                                        FallbackIcon={MiniCartIcon}
                                        iconStyles={{
                                            ...styles.iconButton,
                                            'aria-hidden': 'true',
                                            focusable: 'false'
                                        }}
                                        fallbackProps={{
                                            ...styles.iconButton,
                                            'aria-hidden': 'true',
                                            focusable: 'false'
                                        }}
                                        colorFilter={cmsIconColorFilter}
                                    />
                                    {basket && totalItems > 0 && (
                                        <Text {...styles.cartCount}>({totalItems})</Text>
                                    )}
                                </>
                            }
                            {...styles.cartButton}
                            {...(cartIconLinkProps || {
                                onClick: (e) => {
                                    if (e) e.preventDefault()

                                    // In Optimizely preview mode we want reviewers to
                                    // be able to open the minicart from the cart page to
                                    // inspect the previewed content, so skip the
                                    // "already on cart" toast when a preview session is active.
                                    if (isCart && !getPreviewSession()) {
                                        if (cartToastIdRef.current === null) {
                                            cartToastIdRef.current = showCustomToast({
                                                message: intl.formatMessage({
                                                    id: 'header.cart.already_on_cart_page',
                                                    defaultMessage: 'You are currently on your Cart page.'
                                                }),
                                                onClose: () => {
                                                    cartToastIdRef.current = null
                                                }
                                            })
                                        }
                                    } else if (isDesktop) {
                                        onOpenMiniCartOpen()
                                    } else {
                                        onMyCartClick()
                                    }
                                }
                            })}
                        />
                    </Flex>
                </Flex>
            </Box>
            </Box>
        </Box>
    )
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onStoreLocatorClick: PropTypes.func,
    isCart: PropTypes.bool,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ])
}

export default Header
